import {
  ChangeDetectionStrategy,
  Component,
  effect,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';
import { form, FormField as SignalFormField, required } from '@angular/forms/signals';
import { SignalCheckbox, SignalFormDirective, SignalNumberField, SignalTextField } from '@vallift/ngl';
import type { FormField } from '../../../../models/built-form';

interface ImageConfigModel {
  label: string;
  helpText: string;
  maxFileSize: number | null;
  maxImages: number | null;
  required: boolean;
}

@Component({
  selector: 'app-image-config-view',
  standalone: true,
  imports: [
    SignalFormField,
    SignalTextField,
    SignalCheckbox,
    SignalNumberField,
    SignalFormDirective,
  ],
  templateUrl: './image-config-view.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ImageConfigViewComponent {
  readonly field = input.required<FormField>();
  readonly fieldChange = output<FormField>();

  readonly model = signal<ImageConfigModel>({
    label: '',
    helpText: '',
    maxFileSize: null,
    maxImages: null,
    required: false,
  });

  readonly configForm = form(this.model, (schema) => {
    required(schema.label);
    required(schema.maxFileSize);
    required(schema.maxImages);
  });

  private lastFieldId: string | null = null;
  private suppressEmit = false;

  constructor() {
    effect(() => {
      const next = this.field();
      if (next.id === this.lastFieldId) {
        return;
      }
      this.lastFieldId = next.id;
      this.suppressEmit = true;
      const validation = next.validation ?? {};
      this.model.set({
        label: next.label,
        helpText: next.helpText ?? '',
        maxFileSize: next.maxFileSize ?? null,
        maxImages: next.maxImages ?? null,
        required: next.required ?? validation.required ?? false,
      });
    });

    effect(() => {
      const draft = this.model();
      if (this.suppressEmit) {
        this.suppressEmit = false;
        return;
      }
      const field = untracked(() => this.field());
      if (!field || field.id !== this.lastFieldId) {
        return;
      }
      this.fieldChange.emit({
        ...field,
        label: draft.label.trim(),
        helpText: draft.helpText.trim() || undefined,
        required: draft.required,
        validation: {
          required: draft.required,
        },
        options: undefined,
        headingLevel: undefined,
        spacerSize: undefined,
      });
    });
  }
}
